// src/auth/listeners.js
import { Hub } from 'aws-amplify';
import { Auth } from 'aws-amplify';

/**
 * Extract the token expiry time from a JWT token
 * @param {string} token - JWT token
 * @returns {number|null} - Expiry time in milliseconds, or null if invalid
 */
const getTokenExpiryTime = (token) => {
    try {
        if (!token) return null;
        const payload = token.split('.')[1];
        const decoded = JSON.parse(atob(payload));
        return decoded.exp ? decoded.exp * 1000 : null; // Convert to milliseconds
    } catch (error) {
        console.warn("Failed to decode token expiry:", error);
        return null;
    }
};

/**
 * Attempt to refresh the session
 * @returns {Promise<'SUCCESS'|'AUTH_ERROR'|'NETWORK_ERROR'>}
 */
const attemptSessionRefresh = async () => {
    try {
        // Check network first
        if (!navigator.onLine) {
            console.log("Network offline, skipping session refresh attempt");
            return 'NETWORK_ERROR';
        }

        // Auth.currentSession() automatically uses the refresh token
        // if the access token is expired but refresh token is valid
        const session = await Auth.currentSession();
        if (session && session.isValid()) {
            console.log("Session refreshed successfully");
            return 'SUCCESS';
        }
        return 'AUTH_ERROR'; // Should generally not reach here if session is invalid, it throws
    } catch (error) {
        // Distinguish between network errors and actual auth failures
        const isNetworkError =
            error.message === 'Network Error' ||
            error.code === 'NetworkError' ||
            error.name === 'NetworkError' ||
            !navigator.onLine;

        if (isNetworkError) {
            console.warn("Session refresh skipped due to network error:", error);
            return 'NETWORK_ERROR';
        }

        // "No current user" or "The user is not authenticated" means the refresh token is dead
        // NotAuthorizedException also means the refresh token is invalid/revoked
        const isAuthError =
            error === 'No current user' ||
            error.message === 'The user is not authenticated' ||
            error.code === 'NotAuthorizedException';

        if (isAuthError) {
            console.error("Session refresh failed - Auth Invalid:", error);
            return 'AUTH_ERROR';
        }

        console.warn("Unknown error during session refresh, treating as non-fatal:", error);
        return 'NETWORK_ERROR'; // Treat unknown errors conservatively
    }
};

export const registerAuthListeners = () => {
    Hub.listen('auth', async ({ payload }) => {
        switch (payload.event) {
            case 'tokenRefresh':
                console.log('Token refreshed successfully via Hub');
                break;

            case 'tokenRefresh_failure':
                console.log('Token refresh failed via Hub — attempting manual refresh');
                // Don't immediately sign out - try one more refresh attempt
                const result = await attemptSessionRefresh();

                // Only sign out if we are SURE it's an auth error (refresh token invalid)
                // If it's a network error, keep the session data and try again later
                if (result === 'AUTH_ERROR') {
                    console.log('Manual refresh confirmed Auth Error — signing out');
                    Auth.signOut();
                } else if (result === 'NETWORK_ERROR') {
                    console.log('Manual refresh failed due to network — keeping session for retry');
                }
                break;

            case 'sessionExpired':
                console.log('Session expired event — attempting refresh');
                const refreshResult = await attemptSessionRefresh();

                if (refreshResult === 'AUTH_ERROR') {
                    console.error("Failed to refresh session (Auth Error) — signing out");
                    Auth.signOut();
                } else if (refreshResult === 'NETWORK_ERROR') {
                    console.log("Failed to refresh session due to network — keeping session");
                }
                break;

            default:
                break;
        }
    });

    // Add online listener to retry auth when network comes back
    if (typeof window !== 'undefined') {
        window.addEventListener('online', async () => {
            console.log("Network back online — re-validating session");
            const result = await attemptSessionRefresh();
            if (result === 'AUTH_ERROR') {
                Auth.signOut();
            }
        });
    }
};

let visibilityDebounceTimer = null;

export const startVisibilityWatcher = () => {
    const checkAndRefreshAuth = async () => {
        // 1. If we are offline, do nothing. Wait for 'online' event.
        if (!navigator.onLine) {
            console.log("Offline on visibility change — skipping auth check");
            return;
        }

        try {
            const session = await Auth.currentSession();
            if (session && session.isValid()) {
                console.log("Session valid on tab visibility");
                return;
            }
        } catch (error) {
            console.log("Session check failed on visibility change, attempting refresh...");
        }

        // 2. Attempt refresh carefully
        const result = await attemptSessionRefresh();

        if (result === 'AUTH_ERROR') {
            console.log("Session refresh failed on visibility change (Auth Error) — signing out");
            Auth.signOut();
        } else if (result === 'NETWORK_ERROR') {
            console.log("Session refresh failed on visibility change (Network) — ignoring");
        }
    };

    document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
            // Debounce rapid visibility changes
            if (visibilityDebounceTimer) {
                clearTimeout(visibilityDebounceTimer);
            }
            visibilityDebounceTimer = setTimeout(() => {
                checkAndRefreshAuth();
                visibilityDebounceTimer = null;
            }, 500); // Wait 500ms before checking to avoid rapid-fire checks
        }
    });
};

let proactiveRefreshTimer = null;

/**
 * Proactively refresh tokens before they expire
 * This replaces the old startIntervalSessionCheck which was too aggressive
 */
export const startProactiveTokenRefresh = () => {
    const REFRESH_BUFFER_MS = 5 * 60 * 1000; // Refresh 5 minutes before expiry
    const CHECK_INTERVAL_MS = 60 * 1000; // Check every minute
    const MIN_REFRESH_INTERVAL_MS = 30 * 1000; // Don't refresh more often than every 30 seconds

    let lastRefreshTime = 0;

    const scheduleRefresh = async () => {
        // Skip if offline
        if (!navigator.onLine) return;

        try {
            const session = await Auth.currentSession();
            if (!session || !session.isValid()) {
                console.log("No valid session for proactive refresh");
                return;
            }

            const accessToken = session.getAccessToken();
            const idToken = session.getIdToken();

            // Get the earliest expiry time between access and id tokens
            const accessExpiry = getTokenExpiryTime(accessToken.getJwtToken());
            const idExpiry = getTokenExpiryTime(idToken.getJwtToken());

            const earliestExpiry = Math.min(
                accessExpiry || Infinity,
                idExpiry || Infinity
            );

            if (earliestExpiry === Infinity) {
                return;
            }

            const now = Date.now();
            const timeUntilExpiry = earliestExpiry - now;
            const timeUntilRefresh = timeUntilExpiry - REFRESH_BUFFER_MS;

            // Only log if close to expiry to reduce noise
            if (timeUntilExpiry < REFRESH_BUFFER_MS * 2) {
                console.log(`Token expires in ${Math.round(timeUntilExpiry / 60000)} minutes`);
            }

            // If token expires within buffer time, refresh now
            if (timeUntilRefresh <= 0) {
                const timeSinceLastRefresh = now - lastRefreshTime;
                if (timeSinceLastRefresh < MIN_REFRESH_INTERVAL_MS) {
                    return;
                }

                console.log("Token expiring soon, proactively refreshing...");
                const result = await attemptSessionRefresh();

                if (result === 'SUCCESS') {
                    lastRefreshTime = Date.now();
                    console.log("Proactive token refresh successful");
                }
                // Note: We intentionally DO NOT sign out here even if AUTH_ERROR
                // We let the natural expiry or visibility watcher handle the final logout
                // to be less intrusive during active usage.
            }
        } catch (error) {
            // Silent catch for proactive check
        }
    };

    // Clear any existing timer
    if (proactiveRefreshTimer) {
        clearInterval(proactiveRefreshTimer);
    }

    // Check periodically
    proactiveRefreshTimer = setInterval(scheduleRefresh, CHECK_INTERVAL_MS);

    // Also check immediately on startup
    setTimeout(scheduleRefresh, 5000); // Wait 5s after app start
};

// Keep backward compatibility - alias the old function name
export const startIntervalSessionCheck = startProactiveTokenRefresh;
